/**
 * Comprehensive Analytics API Service
 * Handles communication with comprehensive analytics backend endpoint
 */

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1') + '/sis-integration';

export interface IndicatorResult {
  area_name: string;
  indicator_code: string;
  indicator_name: string;
  indicator_value: number;
  category: string;
  source: string;
  period: string;
  year?: string;
}

export interface ComprehensiveAnalyticsData {
  group_by: string;
  academic_year: string;
  moe_indicators: IndicatorResult[];
  infrastructure: IndicatorResult[];
  enrollment: IndicatorResult[];
  staff: IndicatorResult[];
  summary: {
    total_indicators: number;
    total_areas: number;
  };
}

export interface ComprehensiveAnalyticsResponse {
  status: string;
  data: ComprehensiveAnalyticsData;
}

/**
 * Get all indicators summary across the system
 */
export const getComprehensiveAnalytics = async (
  groupBy: string = 'district',
  academicYear: string = '2026'
): Promise<ComprehensiveAnalyticsResponse> => {
  const params = new URLSearchParams({
    group_by: groupBy,
    academic_year: academicYear,
  });

  const response = await fetch(
    `${API_BASE_URL}/comprehensive-analytics/?${params}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to fetch comprehensive analytics: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get indicators by category
 */
export const getIndicatorsByCategory = (
  data: ComprehensiveAnalyticsData,
  category: string
): IndicatorResult[] => {
  const allIndicators = [
    ...data.moe_indicators,
    ...data.infrastructure,
    ...data.enrollment,
    ...data.staff,
  ];
  
  return allIndicators.filter(indicator => 
    indicator.category.toLowerCase() === category.toLowerCase()
  );
};

/**
 * Get indicators by area
 */
export const getIndicatorsByArea = (
  data: ComprehensiveAnalyticsData,
  areaName: string
): IndicatorResult[] => {
  const allIndicators = [
    ...data.moe_indicators,
    ...data.infrastructure,
    ...data.enrollment,
    ...data.staff,
  ];
  
  return allIndicators.filter(indicator => 
    indicator.area_name.toLowerCase() === areaName.toLowerCase()
  );
};

/**
 * Get unique areas from the data
 */
export const getUniqueAreas = (data: ComprehensiveAnalyticsData): string[] => {
  const allIndicators = [
    ...data.moe_indicators,
    ...data.infrastructure,
    ...data.enrollment,
    ...data.staff,
  ];
  
  const areaSet = new Set(allIndicators.map(indicator => indicator.area_name));
  const areas = Array.from(areaSet);
  return areas.sort();
};

/**
 * Get unique categories from the data
 */
export const getUniqueCategories = (data: ComprehensiveAnalyticsData): string[] => {
  const allIndicators = [
    ...data.moe_indicators,
    ...data.infrastructure,
    ...data.enrollment,
    ...data.staff,
  ];
  
  const categorySet = new Set(allIndicators.map(indicator => indicator.category));
  const categories = Array.from(categorySet);
  return categories.sort();
};

/**
 * Calculate category totals
 */
export const getCategoryTotals = (
  data: ComprehensiveAnalyticsData
): Record<string, { total: number; count: number }> => {
  const allIndicators = [
    ...data.moe_indicators,
    ...data.infrastructure,
    ...data.enrollment,
    ...data.staff,
  ];
  
  const totals: Record<string, { total: number; count: number }> = {};
  
  allIndicators.forEach(indicator => {
    const category = indicator.category;
    if (!totals[category]) {
      totals[category] = { total: 0, count: 0 };
    }
    totals[category].total += indicator.indicator_value;
    totals[category].count += 1;
  });
  
  return totals;
};

/**
 * Export comprehensive analytics to CSV
 */
export const exportComprehensiveAnalytics = (
  data: ComprehensiveAnalyticsData,
  filename?: string
): void => {
  const allIndicators = [
    ...data.moe_indicators,
    ...data.infrastructure,
    ...data.enrollment,
    ...data.staff,
  ];

  if (allIndicators.length === 0) {
    throw new Error('No data to export');
  }

  const csvHeaders = [
    'Area',
    'Indicator Code',
    'Indicator Name',
    'Value',
    'Category',
    'Source',
    'Period',
    'Year',
  ];

  const csvRows = allIndicators.map(indicator => [
    indicator.area_name,
    indicator.indicator_code,
    indicator.indicator_name,
    indicator.indicator_value.toString(),
    indicator.category,
    indicator.source,
    indicator.period,
    indicator.year || data.academic_year,
  ]);

  const csvContent = [csvHeaders, ...csvRows]
    .map(row => row.map(field => `"${field}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename || `comprehensive_analytics_${data.group_by}_${data.academic_year}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Format large numbers for display
 */
export const formatNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

/**
 * Get category color for consistent UI display
 */
export const getCategoryColor = (category: string): string => {
  const categoryColors: Record<string, string> = {
    'moe standard': '#1976d2',
    'infrastructure': '#2e7d32',
    'enrollment': '#ed6c02',
    'staff': '#9c27b0',
    'academic': '#d32f2f',
    'demographic': '#455a64',
  };
  
  return categoryColors[category.toLowerCase()] || '#666666';
};

export default {
  getComprehensiveAnalytics,
  getIndicatorsByCategory,
  getIndicatorsByArea,
  getUniqueAreas,
  getUniqueCategories,
  getCategoryTotals,
  exportComprehensiveAnalytics,
  formatNumber,
  getCategoryColor,
};